import React, { useCallback, useMemo, memo, useState, useEffect, useRef } from 'react';
import { Counter, LeftArrowContainer, RightArrowContainer, StyledImage, StyledImageContainer, StyledCarouselWrapper, BgPictureContainer, } from './styles';
import { Slider, Slide, DotGroup } from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import LeftNavigator from '../icons/left-navigator';
import RightNavigator from '../icons/right-navigator';
import CarouselDots from './carousel-dots';
import OverlayModal from './overlay-modal';
import { useCarousel } from './hooks/use-carousel';
import FullScreenModal from './full-screen-modal';
import { getImageUrl } from './get-image-url';
var GalleryCarousel = memo(function (_a) {
    var images = _a.images, _b = _a.skipFullScreen, skipFullScreen = _b === void 0 ? false : _b, _c = _a.skipOverlay, skipOverlay = _c === void 0 ? false : _c, imageBorder = _a.imageBorder, imageBorderRadius = _a.imageBorderRadius, onSlideChange = _a.onSlideChange, parentRef = _a.parentRef, showDots = _a.showDots, showCountTile = _a.showCountTile, showArrows = _a.showArrows, footer = _a.footer, header = _a.header, autoPlay = _a.autoPlay;
    var _d = useState(false), isFullScreen = _d[0], setFullScreen = _d[1];
    var _e = useState(false), isOverlayOpen = _e[0], setOverlay = _e[1];
    var isDraggingRef = useRef(false);
    var dragStartPos = useRef({ x: 0, y: 0 });
    var _f = useCarousel(parentRef, {
        onSlideChange: onSlideChange,
    }), currentSlide = _f.currentSlide, prev = _f.prev, next = _f.next, goToSlide = _f.goToSlide, resumeAutoplay = _f.resumeAutoplay, stopAutoplay = _f.stopAutoplay;
    useEffect(function () {
        if (!autoPlay) {
            return;
        }
        if (isOverlayOpen || isFullScreen) {
            stopAutoplay();
        }
        else {
            resumeAutoplay();
        }
    }, [isOverlayOpen, isFullScreen, autoPlay, stopAutoplay, resumeAutoplay]);
    var handleOnItemClick = useCallback(function () {
        if (!skipFullScreen) {
            setFullScreen(true);
        }
        else if (!skipOverlay) {
            setOverlay(true);
        }
    }, [skipFullScreen, skipOverlay]);
    var handleMouseDown = useCallback(function (event) {
        dragStartPos.current = { x: event.clientX, y: event.clientY };
        isDraggingRef.current = false;
    }, []);
    var handleMouseMove = useCallback(function (event) {
        var dx = event.clientX - dragStartPos.current.x;
        var dy = event.clientY - dragStartPos.current.y;
        if (!isDraggingRef.current && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
            isDraggingRef.current = true;
        }
    }, []);
    var handleMouseEnter = useCallback(function () {
        stopAutoplay();
    }, [stopAutoplay]);
    var handleMouseLeave = useCallback(function () {
        resumeAutoplay();
    }, [resumeAutoplay]);
    var handleMouseUp = useCallback(function () {
        if (!isDraggingRef.current) {
            handleOnItemClick();
        }
        isDraggingRef.current = false;
    }, [handleOnItemClick]);
    var imageUrlsMap = useMemo(function () {
        return images.map(function (image) { return ({
            small: getImageUrl('small-mobile', image),
            medium: getImageUrl('medium-mobile', image),
            large: getImageUrl('large-mobile', image),
            desktop: getImageUrl('desktop', image),
            wideScreen: getImageUrl('wide-screen', image),
        }); });
    }, [images]);
    var renderedImages = useMemo(function () {
        return images.map(function (image, index) {
            var imageUrls = imageUrlsMap[index];
            return (React.createElement(StyledImageContainer, { key: "image_carousel_" + index, onMouseDown: handleMouseDown, onMouseMove: handleMouseMove, onMouseUp: handleMouseUp, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, imageBorder: imageBorder, imageBorderRadius: imageBorderRadius },
                React.createElement(BgPictureContainer, null,
                    imageUrls.wideScreen && (React.createElement("source", { media: "(min-width:1024px)", srcSet: imageUrls.wideScreen })),
                    imageUrls.desktop && React.createElement("source", { media: "(min-width:640px)", srcSet: imageUrls.desktop }),
                    imageUrls.large && React.createElement("source", { media: "(min-width:480px)", srcSet: imageUrls.large }),
                    imageUrls.medium && React.createElement("source", { media: "(min-width:320px)", srcSet: imageUrls.medium }),
                    imageUrls.small && (React.createElement(StyledImage, { alt: image.caption, src: imageUrls.small, imageBorder: imageBorder, imageBorderRadius: imageBorderRadius, loading: index < 1 ? 'eager' : 'lazy', decoding: index < 1 ? 'sync' : 'async', fetchpriority: index < 1 ? 'high' : 'low' })))));
        });
    }, [
        images,
        imageUrlsMap,
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        handleMouseEnter,
        handleMouseLeave,
        imageBorder,
        imageBorderRadius,
    ]);
    return (React.createElement(StyledCarouselWrapper, null,
        React.createElement(Slider, null, renderedImages.map(function (renderedImage, index) { return (React.createElement(Slide, { index: index, key: "slide_" + index }, renderedImage)); })),
        !!showDots && (React.createElement(DotGroup, { renderDots: function (_a) {
                var currentSlide = _a.currentSlide, totalSlides = _a.totalSlides;
                return (React.createElement(CarouselDots, { currentIndex: currentSlide, totalImages: totalSlides }));
            } })),
        !!showArrows && (React.createElement(React.Fragment, null,
            React.createElement(LeftArrowContainer, { onClick: prev },
                React.createElement(LeftNavigator, null)),
            React.createElement(RightArrowContainer, { onClick: next },
                React.createElement(RightNavigator, null)))),
        !!showCountTile && React.createElement(Counter, null, currentSlide + 1 + " / " + images.length),
        React.createElement(FullScreenModal, { images: images, isFullScreenOpen: isFullScreen, setFullScreen: setFullScreen, header: header, footer: footer, setOverlay: setOverlay, setSelectedImage: goToSlide, currentIndex: currentSlide }),
        React.createElement(OverlayModal, { isOverlayOpen: isOverlayOpen, setOverlay: setOverlay, images: images, initialSlide: currentSlide, setSelectedImage: goToSlide })));
});
export default GalleryCarousel;
